import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Award, Plus, Trash2, X, Save, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const BadgeManager = ({ token, users }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(null);
  const [newBadge, setNewBadge] = useState({
    name: "",
    description: "",
    icon: "🏆",
    color: "bg-yellow-100 text-yellow-700"
  });

  const colorOptions = [
    { label: "Gold", value: "bg-yellow-100 text-yellow-700" },
    { label: "Blue", value: "bg-blue-100 text-blue-700" },
    { label: "Green", value: "bg-green-100 text-green-700" },
    { label: "Purple", value: "bg-purple-100 text-purple-700" },
    { label: "Red", value: "bg-red-100 text-red-700" },
    { label: "Cyan", value: "bg-cyan-100 text-cyan-700" },
    { label: "Orange", value: "bg-orange-100 text-orange-700" }
  ];

  const iconOptions = ["🏆", "⭐", "🎖️", "🌊", "🐠", "🦈", "🐢", "🔬", "📸", "✅", "🎯", "💎"];

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const res = await axios.get(`${API}/badges`);
      setBadges(res.data);
    } catch (error) {
      toast.error("Failed to load badges");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBadge = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/badges`, newBadge, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Badge created!");
      setShowForm(false);
      setNewBadge({ name: "", description: "", icon: "🏆", color: "bg-yellow-100 text-yellow-700" });
      fetchBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create badge");
    }
  };

  const handleDeleteBadge = async (badgeId) => {
    if (!confirm("Delete this badge? It will be removed from all users.")) return;
    
    try {
      await axios.delete(`${API}/admin/badges/${badgeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Badge deleted");
      fetchBadges();
    } catch (error) {
      toast.error("Failed to delete badge");
    }
  };

  const handleAwardBadge = async (userId, badgeId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/badges/${badgeId}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Badge awarded!");
      setShowAwardModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to award badge");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-500" />
          Member Badges
        </CardTitle>
        <Button onClick={() => setShowForm(!showForm)} size="sm">
          {showForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
          {showForm ? "Cancel" : "Create Badge"}
        </Button>
      </CardHeader>
      <CardContent>
        {/* Create Badge Form */}
        {showForm && (
          <form onSubmit={handleCreateBadge} className="mb-6 p-4 bg-yellow-50 rounded-lg space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Badge Name</Label>
                <Input
                  value={newBadge.name}
                  onChange={(e) => setNewBadge({...newBadge, name: e.target.value})}
                  placeholder="e.g., Marine Biologist"
                  required
                />
              </div>
              <div>
                <Label>Icon</Label>
                <div className="flex gap-1 flex-wrap">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setNewBadge({...newBadge, icon})}
                      className={`p-2 text-xl rounded ${newBadge.icon === icon ? "bg-yellow-200 ring-2 ring-yellow-400" : "hover:bg-gray-100"}`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newBadge.description}
                onChange={(e) => setNewBadge({...newBadge, description: e.target.value})}
                placeholder="What this badge represents..."
                required
              />
            </div>
            <div>
              <Label>Color Theme</Label>
              <div className="flex gap-2 flex-wrap">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setNewBadge({...newBadge, color: color.value})}
                    className={`px-3 py-1 rounded ${color.value} ${newBadge.color === color.value ? "ring-2 ring-offset-2 ring-gray-400" : ""}`}
                  >
                    {color.label}
                  </button>
                ))}
              </div>
            </div>
            <Button type="submit" className="bg-yellow-500 hover:bg-yellow-600">
              <Save className="w-4 h-4 mr-2" />
              Create Badge
            </Button>
          </form>
        )}

        {/* Badge List */}
        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading badges...</p>
        ) : badges.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No badges created yet</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {badges.map((badge) => (
              <div key={badge.id} className={`p-4 rounded-lg border ${badge.color}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{badge.icon}</span>
                    <div>
                      <h4 className="font-semibold">{badge.name}</h4>
                      <p className="text-sm opacity-75">{badge.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowAwardModal(badge)}
                      title="Award to user"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteBadge(badge.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Award Badge Modal */}
        {showAwardModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">{showAwardModal.icon}</span>
                Award "{showAwardModal.name}"
              </h3>
              <p className="text-sm text-gray-600 mb-4">Select a user to award this badge to:</p>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {users?.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleAwardBadge(user.id, showAwardModal.id)}
                    className="w-full p-3 text-left border rounded-lg hover:bg-gray-50 flex items-center gap-3"
                  >
                    {user.profile_pic ? (
                      <img
                        src={getImageUrl(user.profile_pic)}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 font-semibold">
                        {user.name?.charAt(0) || "?"}
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </button>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => setShowAwardModal(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BadgeManager;
